// @givmo/compliance — the module's route constants (CCM-WEB1; re-based to `/compliance` at
// CCM-MOD1B; re-composed as Calendar · Filings · Documents at CCM-R1-INTAKE (a)).
//
// Route constants live here so the router, the nav shell, and every in-feature link agree on one
// spelling. The module is a PATH inside a class console — `<console>/compliance` — never a host of
// its own (D-CCM-1 as amended 2026-08-30). The Phase-2 filing-register PATH family is retired: the
// charity host keeps those six old addresses as replace-redirects into this family
// (`console-routes.ts` there names them), and nothing here does — the copy-law rail pins that.
//
// ══ THE MODULE'S PAGES (set C Q1, D-CCM-21) ════════════════════════════════════════════════════
//
//   /compliance                         the Calendar — the module's landing (Q1);
//   /compliance/filings                 Filings, grouped by family (frames 13–17);
//   /compliance/filings/<filing id>     Filings with one filing's panel open (frames 18–20), where
//                                       the id is an obligation's or an own filing's (both uuids);
//   /compliance/filings/<id>/rule       the same sheet showing the rule behind a library filing
//                                       (design go D2): the one view that keeps Q10's line;
//   /compliance/filings/<id>/form       the filing's form, prepared from the organization's records
//                                       and answers (CCM-R1-DOCS; slice 2 §5.1, frames 31–37);
//   /compliance/filings/<id>/send       the signed filing's send review: its package, its email, and
//                                       the organization's authorization to send it, then what
//                                       happened (CCM-R1-SUBMIT-WEB; slice 3 §5–§6, frames 62–73);
//   /compliance/sign/<request id>       a request a person was asked to sign (CCM-R1-SIGN-WEB; slice
//                                       2 §6(b), frames 38–40) — the address the backend's
//                                       invitation email links to, byte for byte; signing is always
//                                       the person pressing Sign here: no AI signs (D-CCM-31);
//   /compliance/documents               Documents (frames 06, 12).
//
// The Dates page and the recorded-facts page leave the navigation (Q1; recorded facts come back with
// the fact store, D-CCM-14). Their addresses are {@link COMPLIANCE_RETIRED_ROUTES}, each a host
// replace-redirect onto the Calendar, so a bookmark or a mailed link still lands somewhere true. The
// hidden review queue's address is the review's own (CCM-R1-REVIEW-WEB), and its page retired with
// the legacy queue (CCM-R1-REVIEW-LEGACY-RETIRE).
//
// ⚠ VOCABULARY. Every path stays in the `compliance` family. The status-shaped and stance-shaped
// alternatives are banned as wire names, code identifiers AND URLs — a URL is the most quotable
// identifier a product has, and a status-shaped one in an address bar is a claim whatever the page
// beneath it says. The banned spellings are enumerated in `compliance-copy-law.test.ts`, which is
// the only file in this feature permitted to write them down.
//
// ⚠ TWO RESERVED STATIC ALIASES (`signIn`, `team`) sit beside the pages. The shell serves every
// mounted module's door and Team page through one `$module/sign-in` and one `$module/team` route —
// but this module has a FLAT resource-id route (`/compliance/$obligationId`, today's obligation
// page), and a route whose first segment is static outranks one whose first segment is a parameter,
// so without the aliases `/compliance/sign-in` and `/compliance/team` would resolve to an obligation
// titled "sign-in" or "team". A host registers each alias onto the SAME component the generic route
// renders; they add no page of their own. (The Fundraisers module carries the identical pair.)

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62; the GO's O5): the search key that opens a filing's form or send review
 * for one year, `?cycle=<that year's cycle id>` — unambiguous where a rule has two deadlines a year.
 * Absent, the pages take the year the filing's next date names.
 */
export const FILING_YEAR_SEARCH_KEY = 'cycle';

/** The module's root path segment, without a slash — the per-module door's vocabulary word. */
const MODULE_PATH = '/compliance';

export const COMPLIANCE_ROUTES = {
  /**
   * The Calendar — the module's landing (set C Q1). The backend's reminder emails link here, as the
   * destination of the organization's deep link (CCM-REMIND-LINK-ORG), so the spelling is a wire
   * contract, not a choice.
   */
  calendar: MODULE_PATH,
  /** Filings: every filing the organization makes, grouped by family. */
  filings: `${MODULE_PATH}/filings`,
  /** One filing's panel, over Filings: an obligation's id or an own filing's. */
  filing: (filingId: string): string => `${MODULE_PATH}/filings/${encodeURIComponent(filingId)}`,
  /** The rule behind a library filing, in the same sheet (design go D2), reached from its source line. */
  filingRule: (filingId: string): string =>
    `${MODULE_PATH}/filings/${encodeURIComponent(filingId)}/rule`,
  /**
   * The filing's form, as Givmo prepares it from the organization's records and answers, for the
   * year (or the filing made once) its open case is for (CCM-R1-DOCS; slice 2 §5.1).
   */
  filingForm: (filingId: string): string =>
    `${MODULE_PATH}/filings/${encodeURIComponent(filingId)}/form`,
  /**
   * The signed filing's send review, for the same case the form is for: the package and the email
   * exactly as they go, the authorization to send them, then what happened (CCM-R1-SUBMIT-WEB).
   */
  filingSend: (filingId: string): string =>
    `${MODULE_PATH}/filings/${encodeURIComponent(filingId)}/send`,
  /**
   * A request someone was asked to sign: its pages, the typed name, the act (CCM-R1-SIGN-WEB). The
   * backend's signing emails link here (`/compliance/sign/{request_id}`), so the spelling is a wire
   * contract, not a choice.
   */
  signing: (requestId: string): string => `${MODULE_PATH}/sign/${encodeURIComponent(requestId)}`,
  /** The documents the organization keeps for its filings. */
  documents: `${MODULE_PATH}/documents`,
  /**
   * The review: what Givmo found when it compared the organization's recorded facts with the rules it
   * evaluates, and the organization's decisions on it (CCM-R1-REVIEW-WEB; D-CCM-51). At the address
   * the hidden queue held (A-C10), outside the navigation: the Calendar's card opens it.
   */
  review: `${MODULE_PATH}/review`,
  /**
   * The old obligation page's address. The host replace-redirects it to that filing's panel
   * (design go D2, approved by Jonathan 2026-09-30 22:46 PDT); Q10's staleness line lives on the
   * panel's rule view now ({@link COMPLIANCE_ROUTES.filingRule}). Kept as the redirect's template.
   */
  obligation: (obligationId: string): string =>
    `${MODULE_PATH}/${encodeURIComponent(obligationId)}`,
  /** Reserved alias: the module's sign-in door, ahead of the flat obligation route. */
  signIn: `${MODULE_PATH}/sign-in`,
  /** Reserved alias: the shell's Team page for this module, ahead of the flat obligation route. */
  team: `${MODULE_PATH}/team`,
} as const;

/** The router param template for one filing's panel. */
export const FILING_PANEL_PATH = `${MODULE_PATH}/filings/$filingId`;

/** The router param template for the rule behind a filing, in the same sheet. */
export const FILING_RULE_PATH = `${MODULE_PATH}/filings/$filingId/rule`;

/** The router param template for a filing's form (the workbench). */
export const FILING_FORM_PATH = `${MODULE_PATH}/filings/$filingId/form`;

/** The router param template for a signed filing's send review. */
export const FILING_SEND_PATH = `${MODULE_PATH}/filings/$filingId/send`;

/**
 * The send page's search for sending it yourself (CCM-R1-SUBMIT-HANDOFF): "Send it yourself
 * instead" adds it, so Back and a shared link keep the page the person chose.
 */
export const SEND_YOURSELF_SEARCH = { view: 'yourself' } as const;

/**
 * The send page's search for sending it again after the agency's address never got it
 * (CCM-R1-RESULTS-WEB; REVIEW-30's a3): "Send it again" on the page and on the panel add it, and the
 * page opens the review, where nothing goes until someone authorizes it.
 */
export const SEND_AGAIN_SEARCH = { view: 'again' } as const;

/** The router param template for a request someone was asked to sign. */
export const SIGNING_PATH = `${MODULE_PATH}/sign/$requestId`;

/** The router param template for the old obligation page's address (a redirect to the panel). */
export const OBLIGATION_DETAIL_PATH = `${MODULE_PATH}/$obligationId`;

/**
 * The module's own addresses that left the navigation (set C Q1, A-C10). A host keeps each as a
 * replace-redirect onto the Calendar — the recorded facts until the fact store (D-CCM-14) — and
 * registers no page at any of them. The review queue's address is the review's own now
 * (`COMPLIANCE_ROUTES.review`, CCM-R1-REVIEW-WEB).
 */
export const COMPLIANCE_RETIRED_ROUTES = {
  /** The Dates page, which the Calendar replaces. */
  deadlines: `${MODULE_PATH}/deadlines`,
  /** The recorded-facts page, back with the fact store (D-CCM-14). */
  recordedFacts: `${MODULE_PATH}/recorded-facts`,
} as const;

/** Stable page roots consumed by host and cross-repo e2e contract rails. */
export const COMPLIANCE_PAGE_TEST_IDS = {
  calendar: 'page-compliance-calendar',
  filings: 'page-compliance-filings',
  documents: 'page-compliance-documents',
  /** A filing's form, prepared (the workbench). */
  filingForm: 'page-compliance-filing-form',
  /** A signed filing's send review. */
  filingSend: 'page-compliance-filing-send',
  /** A request someone was asked to sign. */
  signing: 'page-compliance-signing',
  obligation: 'page-compliance-obligation',
  /** The review, at the hidden queue's address and root (CCM-R1-REVIEW-WEB). */
  review: 'page-compliance-review',
  /** The hidden recorded-facts page keeps its root for the row that brings it back. */
  recordedFacts: 'page-compliance-facts',
} as const;
